import { Reference, useApolloClient } from '@apollo/client'
import {
  AggregatedBalanceRefreshedDocument,
  AttendanceAggregatedBalance,
  AttendanceBalancePool,
  TimesheetAggregatedBalanceFragmentDoc,
} from 'generated/resources/graphql'

import { useSubscription } from 'modules/core/hooks/useSubscription'

import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import AttendanceFeatureUtils from 'modules/attendance/lib/utils/AttendanceFeatures'

interface Props {
  employeeId: number
}

const useSubscriptionAggregatedBalanceRefreshed = ({ employeeId }: Props) => {
  const apolloClient = useApolloClient()
  const { featureByName } = useTimesheetData()

  useSubscription(AggregatedBalanceRefreshedDocument, {
    onData: ({ data: refreshedData }) => {
      const refreshBalance =
        refreshedData.data?.attendanceAggregatedBalanceRefresh?.aggregatedBalance

      if (!refreshBalance || refreshBalance?.employeeId != employeeId) return

      refreshBalance.balances.forEach((aggregatedBalance) => {
        apolloClient.cache.modify({
          id: `AttendanceEmployee:${employeeId}`,
          fields: {
            attendanceAggregatedBalanceConnection(existingConnection, { readField }) {
              const id = `${employeeId}_${aggregatedBalance.startOn}_${aggregatedBalance.endOn}`
              const balancePools: AttendanceBalancePool[] = (
                aggregatedBalance.balancePools || []
              ).map((pool) => ({
                ...pool,
                __typename: 'AttendanceBalancePool',
                usages:
                  pool.usages?.map((usage) => ({
                    ...usage,
                    __typename: 'AttendanceTimeBlock',
                  })) || [],
                transfers:
                  pool.transfers?.map((transfer) => ({
                    ...transfer,
                    __typename: 'AttendanceTimeBlock',
                  })) || [],
              }))

              const balanceWithId: AttendanceAggregatedBalance = {
                ...aggregatedBalance,
                balancePools,
                id: id,
                __typename: 'AttendanceAggregatedBalance',
              }

              const newBalanceRef = apolloClient.cache.writeFragment({
                data: balanceWithId,
                fragment: TimesheetAggregatedBalanceFragmentDoc,
                fragmentName: 'TimesheetAggregatedBalance',
              })

              if (
                (existingConnection.nodes || []).some(
                  (ref: Reference) => readField('id', ref) === id
                )
              ) {
                return existingConnection
              }

              return {
                ...existingConnection,
                nodes: [...existingConnection.nodes, newBalanceRef],
                totalCount: existingConnection.totalCount + 1,
              }
            },
          },
        })
      })
    },
    skip: !AttendanceFeatureUtils.hasBalanceMaterialized(featureByName),
  })
}

export default useSubscriptionAggregatedBalanceRefreshed
